import React, { useContext } from 'react';
import type { AnalysisSubject } from 'venn-utils';
import { MANAGE_DATA_SECTION, useHasFF } from 'venn-utils';
import { BulkAllocationManagementTable, BulkPortfolioManagementTable, UserContext } from 'venn-components';
import { TablePlaceholder } from 'venn-ui-kit';
import PortfolioPolicy from './portfolio/PortfolioPolicy';
import PortfolioInformation from './portfolio/PortfolioInformation';
import type { Portfolio } from 'venn-api';

interface PortfolioManageDataProps {
  subject?: AnalysisSubject;
  onFundUpdated: (fundId: string) => void;
  selectedSection?: MANAGE_DATA_SECTION;
  updatePortfolio: (portfolio: Portfolio) => Promise<void>;
}

const PortfolioManageData = ({
  subject,
  onFundUpdated,
  selectedSection,
  updatePortfolio,
}: PortfolioManageDataProps) => {
  const { hasPermission, hasPermissionInAnyContext } = useContext(UserContext);
  const hasProxyVisibilityFeature = useHasFF('proxy_visibility_ff');
  const canOverrideWorkspaceProxy = hasPermissionInAnyContext('EDIT_WORKSPACE_GLOBAL_PROXY');

  if (subject?.type === 'investment') {
    return null;
  }

  return (
    <>
      {selectedSection === MANAGE_DATA_SECTION.ANALYSIS_RANGE && subject ? (
        <BulkPortfolioManagementTable
          subject={subject}
          onFundUpdated={onFundUpdated}
          canEditProxies={hasPermission('MANAGE_PROXY') && (!hasProxyVisibilityFeature || canOverrideWorkspaceProxy)}
          showLiquidatedInvestmentsToggle={!!subject?.portfolio?.historical}
        />
      ) : null}
      {selectedSection === MANAGE_DATA_SECTION.PORTFOLIO_ALLOCATIONS && subject ? (
        <BulkAllocationManagementTable subject={subject} />
      ) : null}
      {selectedSection === MANAGE_DATA_SECTION.ANALYSIS_RANGE && !subject && <TablePlaceholder />}
      {selectedSection === MANAGE_DATA_SECTION.PORTFOLIO_POLICY && <PortfolioPolicy portfolio={subject?.portfolio} />}
      {selectedSection === MANAGE_DATA_SECTION.PORTFOLIO_INFORMATION && subject?.portfolio ? (
        <PortfolioInformation portfolio={subject.portfolio} updatePortfolio={updatePortfolio} />
      ) : null}
    </>
  );
};

export default PortfolioManageData;
